/**
 * CostImport — the SaaS "Import from Shopify" page, reached from the Data
 * Health `missing_cost` card (pages/DataHealth.jsx).
 *
 * Queues the import on mount, then polls it. The import itself is a
 * background pipeline run: it walks the store's whole Shopify-linked
 * catalogue and writes per item, which is minutes on a large store, so the
 * POST returns as soon as the run is queued and every counter on this page
 * comes from the status poll. Landing here while a run is already in flight
 * adopts that run instead of starting a second one (the server answers
 * `already_running`).
 *
 * The action is idempotent and re-runnable — a merchant who later fills in
 * costs on Shopify can come back and pick them up; items already at the
 * imported value count as unchanged.
 *
 * What a run MEANS — which poll answers count, when it is finished, and why a
 * finished run with no summary is a failure rather than a zero-item success —
 * lives in utils/costImportRun.js.
 */

import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useTabs } from '../tabs/useTabs';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import Tile from '../components/layout/Tile';
import { useCostImportStart, useCostImportStatus } from '../hooks/useCostImport';
import { backgroundRunView } from '../utils/backgroundRun';
import {
  costImportFailureMessage,
  costImportItemError,
  costImportStartMessage,
} from '../utils/costImportRun';
import '../styles/pages.css';

export default function CostImport() {
  const { t } = useTranslation();
  const { openTab } = useTabs();

  const startMutation = useCostImportStart();
  // The run this page is watching. Held so `backgroundRunView` can tell a poll
  // answer about THIS run from the previous one's cached counters.
  const [run, setRun] = useState(null);
  const [startError, setStartError] = useState(null);

  // `pollError` is not optional to read: without it a failed status query
  // leaves this page on a spinner that can never resolve, because the query
  // stops on its own once retries are exhausted and `polled` stays null.
  const { state: polled, error: pollError } = useCostImportStatus({ enabled: run !== null });

  // Fire-once on mount. The ref guards StrictMode's double-invocation in dev;
  // the server would dedupe the second POST as `already_running`, but we'd
  // still burn a round-trip and flicker the state.
  const startedRef = useRef(false);

  const start = () => {
    setStartError(null);
    setRun(null);
    startMutation.mutateAsync()
      .then((data) => setRun({ id: data.run_id, outcome: data.outcome }))
      .catch((err) => {
        console.error('cost-import:run failed:', err.message);
        // `costImportStartMessage` in front of `translateRequestError`: this
        // endpoint's two refusals have copy of their own that says what the
        // merchant must go and fix, and the generic `apiErrors.<code>` lookup
        // would render neither.
        setStartError(costImportStartMessage(err, t));
      });
  };

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    start();
    // `start` closes over stable identities (mutation handle, t); empty deps
    // so a parent re-render never re-fires the import.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goHome = () => openTab('/');

  const {
    pending, queued, queueDelayed, statusUnavailable,
    succeeded, failed, summary, errorCode,
  } = backgroundRunView({
    run, polled, startFailed: !!startError, pollFailed: !!pollError,
  });

  return (
    <div className="page-container">
      <Tile title={t('costImport.title')} subtitle={t('costImport.subtitle')}>
        {pending && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              <CircularProgress size={20} />
              <Typography>
                {queued ? t('costImport.queued') : t('costImport.running')}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {t('costImport.backgroundHint')}
            </Typography>
            {queueDelayed && (
              <Alert severity="info">{t('costImport.queueDelayed')}</Alert>
            )}
            <Box>
              <Button variant="outlined" onClick={goHome}>
                {t('costImport.leaveButton')}
              </Button>
            </Box>
          </Stack>
        )}

        {succeeded && (
          <Stack spacing={2}>
            <Alert severity={summary.errored_fx > 0 ? 'warning' : 'success'}>
              {t('costImport.completed', { synced: summary.synced })}
            </Alert>
            <Box>
              <Typography variant="body2" component="div">
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li>{t('costImport.summary.synced', { count: summary.synced })}</li>
                  {summary.skipped_unchanged > 0 && (
                    <li>{t('costImport.summary.skippedUnchanged', { count: summary.skipped_unchanged })}</li>
                  )}
                  {summary.skipped_no_shopify_cost > 0 && (
                    <li>{t('costImport.summary.skippedNoShopifyCost', { count: summary.skipped_no_shopify_cost })}</li>
                  )}
                  {summary.errored_fx > 0 && (
                    <li>{t('costImport.summary.erroredFx', { count: summary.errored_fx })}</li>
                  )}
                </ul>
              </Typography>
              {summary.errors && summary.errors.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">
                    {t('costImport.summary.errorsHeader')}
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ mt: 1, pl: 2, maxHeight: 200, overflowY: 'auto' }}
                  >
                    {summary.errors.map((err, i) => (
                      <li key={i}>
                        <Typography variant="body2">
                          <strong>{err.item_label}</strong>: {costImportItemError(err, t)}
                        </Typography>
                      </li>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
            <Button variant="contained" onClick={goHome}>
              {t('costImport.doneButton')}
            </Button>
          </Stack>
        )}

        {/* The poll broke, not the run. Severity is `warning`, not `error`,
            and there is no Try again: re-queueing a second import because we
            lost sight of the first is the wrong instruction. */}
        {statusUnavailable && (
          <Stack spacing={2}>
            <Alert severity="warning">{t('costImport.statusUnavailable')}</Alert>
            <Box>
              <Button variant="contained" onClick={goHome}>
                {t('costImport.doneButton')}
              </Button>
            </Box>
          </Stack>
        )}

        {(startError || failed) && (
          <Stack spacing={2}>
            <Alert severity="error">
              {startError || costImportFailureMessage(errorCode, t)}
            </Alert>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" onClick={start}>
                {t('costImport.retryButton')}
              </Button>
              <Button variant="outlined" onClick={goHome}>
                {t('costImport.doneButton')}
              </Button>
            </Stack>
          </Stack>
        )}
      </Tile>
    </div>
  );
}
